import { createTheme } from "@mui/material/styles";

import tokens from "./tokens";

/**
 * MUI theme — consumes the SAME design tokens as Tailwind (src/styles/tokens.cjs)
 * so MUI primitives (Dialog, Select, TextField, Tooltip, Menu, DatePicker,
 * TablePagination …) visually match the Tailwind-styled surfaces.
 *
 * Intentionally NOT set here:
 *  - typography.fontFamily: src/styles/language-fonts.css applies font-family
 *    globally with `!important` per language; setting it here would be overridden
 *    and only add confusion.
 *  - <CssBaseline/>: Tailwind's Preflight (@tailwind base) is already the reset;
 *    adding CssBaseline would double-reset and can perturb the !important cascade.
 */
const { colors, semantic, shadow } = tokens;

export const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary[500],
      dark: colors.primary[700],
      light: colors.primary[100],
      contrastText: "#FFFFFF",
    },
    error: colors.error,
    warning: colors.warning,
    success: colors.success,
    info: colors.info,
    background: {
      default: semantic.background,
      paper: semantic.surface,
    },
    text: {
      primary: semantic.textMain,
      secondary: semantic.textMuted,
    },
    divider: semantic.border,
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: "none", borderRadius: 8, fontWeight: 600 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 12 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12, boxShadow: shadow.lg },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: semantic.border },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.neutral[300] },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primary[500],
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: semantic.surface,
          color: semantic.textMain,
          boxShadow: shadow.md,
          fontSize: 12,
          border: `1px solid ${semantic.border}`,
        },
        arrow: { color: semantic.surface },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { color: semantic.textMuted },
      },
    },
  },
});

export default theme;
